import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Alert from "@mui/material/Alert";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTrashablePromises from "hooks/useTrashablePromises";
import React, { useCallback, useState } from "react";
import { DropResult } from "react-beautiful-dnd";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  IQualification,
  ListRequestOfQualificationStatementFilter,
  ListResponseOfQualificationStatement,
  QualificationStatement,
  QualificationStatementSequenceRequest,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import QualificationStatementForm from "./QualificationStatementForm";
import QualificationStatementPreview from "./QualificationStatementPreview";

type QualificationStatementListProps = {
  qualification: IQualification;
};

const QualificationStatementList = ({
  qualification,
}: QualificationStatementListProps) => {
  const { formatMessage: t } = useIntl();
  const { registerPromise } = useTrashablePromises();
  const { qualificationService } = useGlobalContext();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("sequence");
  const [createOpen, createOpenActions] = useBoolean(false);

  const [
    itemToDelete,
    setItemToDelete,
  ] = useState<QualificationStatement | null>(null);
  const [
    qualificationStatementToEdit,
    setQualificationStatementToEdit,
  ] = useState<QualificationStatement | null>(null);
  const queryClient = useQueryClient();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const DeleteButton = ({ value }: { value: QualificationStatement }) => {
    return (
      <Box textAlign="center">
        <IconButton size="small" onClick={() => setItemToDelete(value)}>
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  };

  const EditButton = ({ value }: { value: QualificationStatement }) => {
    return (
      <Box textAlign="center">
        <IconButton
          size="small"
          onClick={() => {
            setQualificationStatementToEdit(value);
            createOpenActions.setTrue();
          }}
        >
          <EditIcon />
        </IconButton>
      </Box>
    );
  };

  const PreviewTemplateAction = ({
    value,
  }: {
    value: QualificationStatement;
  }) => {
    return <QualificationStatementPreview qualificationStatement={value} />;
  };

  const handleDelete = useCallback(async () => {
    const id = itemToDelete?.id;
    setItemToDelete(null);
    if (id) {
      try {
        await registerPromise(
          qualificationService.deleteQualificationStatement(
            qualification.id,
            id
          )
        );
        queryClient.invalidateQueries([
          "qualificationStatements",
          qualification.id,
        ]);
        setSnackbarMessage(
          new SnackbarMessageProps(t({ id: "qualificationStatementDeleted" }))
        );
      } catch (error) {
        console.log(error);
      }
    }
  }, [
    itemToDelete?.id,
    registerPromise,
    qualificationService,
    queryClient,
    qualification.id,
    t,
  ]);

  const {
    isError,
    data,
    error,
    isFetching,
  } = useQuery<ListResponseOfQualificationStatement>(
    ["qualificationStatements", qualification.id],
    () =>
      qualificationService.getQualificationStatements(
        qualification.id,
        ListRequestOfQualificationStatementFilter.fromJS({
          sortField: "sequence",
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const qualificationStatements = data?.data as QualificationStatement[];

  const cells: Array<TableCellType> = [
    {
      accessor: "qualificationStatementTypeName",
      customSortAccessor: "qualificationStatementType.Name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "qualificationStatementListTypeLabel" }),
      noSort: true,
    },
    {
      accessor: "contentName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "qualificationStatementListContentLabel" }),
      noSort: true,
    },
    {
      accessor: "professionDisplayValue",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "professionLabel" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: PreviewTemplateAction,
      label: "",
      noSort: true,
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: EditButton,
      label: "",
      noSort: true,
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: DeleteButton,
      label: "",
      noSort: true,
    },
  ];

  const onQualificationStatementCreated = () => {
    queryClient.invalidateQueries([
      "qualificationStatements",
      qualification.id,
    ]);

    createOpenActions.setFalse();
    setQualificationStatementToEdit(null);

    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "qualificationStatementCreated" }))
    );
  };

  const onQualificationStatementCancel = () => {
    createOpenActions.setFalse();
    setQualificationStatementToEdit(null);
  };

  const onDragEndHandler = async (result: DropResult) => {
    try {
      if (!result.destination) {
        return;
      }
      await qualificationService.saveQualificationStatementSequence(
        qualification.id,
        new QualificationStatementSequenceRequest({
          qualificationStatementId: result.draggableId,
          sequence: result.destination?.index,
        })
      );
    } catch (error) {
      console.log(error);
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  return (
    <Box>
      <Accordion sx={{ mt: 3 }} defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <T id="qualificationStatementListLabel" />
        </AccordionSummary>
        <AccordionDetails>
          <LazyTable
            {...{
              rowCount: data?.totalRecords ?? 0,
              rows: qualificationStatements ?? [],
              loading: isFetching || itemToDelete !== null,
              allowDragAndDrop: true,
              onDragAndDropEnd: onDragEndHandler,
              dense: true,
              titleComponent: (
                <Box display="flex" justifyContent="flex-end" width="100%">
                  <Button
                    onClick={createOpenActions.setTrue}
                    startIcon={<AddIcon />}
                    data-cy="addStatementButton"
                  >
                    <T id="qualificationStatementFormAddItemButton" />
                  </Button>
                </Box>
              ),
              cells,
              allowPagination: false,
              page,
              setPage,
              rowsPerPage,
              setRowsPerPage,
              order,
              setOrder,
              orderBy,
              setOrderBy,
            }}
          />
        </AccordionDetails>
      </Accordion>

      <DeleteEntityDialog
        dialogTitleKey="qualificationStatementDeleteTitle"
        contentKey="qualificationStatementDeleteQuestion"
        open={!!itemToDelete}
        handleDelete={handleDelete}
        onCancel={() => setItemToDelete(null)}
      />

      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <QualificationStatementForm
          qualification={qualification}
          qualificationStatement={qualificationStatementToEdit}
          title={
            qualificationStatementToEdit !== null
              ? t({ id: "qualificationStatementFormEditTitle" })
              : t({ id: "qualificationStatementFormAddTitle" })
          }
          onSubmit={onQualificationStatementCreated}
          onCancel={onQualificationStatementCancel}
        />
      </Dialog>
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Box>
  );
};

export default QualificationStatementList;
